import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Pencil, Play, RotateCcw, Square, Trash2, Shuffle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtBytes, fmtBps, fmtDur, fmtTime, BACKEND } from "@/lib/api";
import { UsageCharts } from "@/components/UsageCharts";
import { usePoll, HealthBadge, Panel, Pill, Stat, UrlRow, LEVEL_TONE, StatusDot } from "@/components/common";
import { HlsPlayer } from "@/components/HlsPlayer";
import { ChannelForm } from "@/components/ChannelForm";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger, AlertDialogDescription } from "@/components/ui/alert-dialog";

function Ladder({ c, act }) {
  const r = c.runtime || {};
  return (
    <>
    <table className="w-full text-xs" data-testid="source-ladder-table">
      <thead><tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-800">
        {["#", "Source", "Health", "7d score", "Detected", "Fails", "Bench", "Last error", ""].map((h) => <th key={h} className="py-2 pr-3 font-semibold">{h}</th>)}
      </tr></thead>
      <tbody>
        {c.sources.map((s, i) => {
          const st = (r.sources || [])[i] || {};
          const sc = (c.source_scores || [])[i];
          const active = r.active_source === i + 1;
          const health = !s.enabled ? "grey" : active ? (i === 0 ? "green" : "amber") : st.benched_for > 0 ? "red" : st.probe_ok === false ? "red" : "grey";
          return (
            <tr key={i} data-testid={`ladder-row-${i + 1}`} className={`border-b border-slate-800/60 ${active ? "bg-cyan-500/5" : ""}`}>
              <td className="py-2 pr-3 font-mono text-cyan-400">{i + 1}</td>
              <td className="py-2 pr-3 max-w-[280px]">
                <div className="text-slate-200 flex items-center gap-2">{s.label || "—"} {active && <Pill tone="cyan">ACTIVE</Pill>} {!s.enabled && <Pill>disabled</Pill>}</div>
                <div className="font-mono text-[11px] text-slate-500 truncate" title={s.url}>{s.url}</div>
              </td>
              <td className="py-2 pr-3"><StatusDot health={health} testid={`ladder-health-${i + 1}`} /> {st.healthy_for != null && !active && <span className="font-mono text-[11px] text-emerald-400 ml-1">ok {fmtDur(st.healthy_for)}</span>}</td>
              <td className="py-2 pr-3">{sc ? <Pill tone={{ good: "green", fair: "amber", weak: "red" }[sc.grade]} testid={`source-score-${i + 1}`}>{sc.score} · {sc.grade}</Pill> : "—"}{sc && <div className="font-mono text-[10px] text-slate-500 mt-0.5">{sc.failures} failure(s)</div>}</td>
              <td className="py-2 pr-3 font-mono text-slate-400">{[st.kind, st.video, st.audio].filter(Boolean).join(" · ") || "—"}</td>
              <td className="py-2 pr-3 font-mono text-slate-300">{st.fails ?? 0}</td>
              <td className={`py-2 pr-3 font-mono ${st.benched_for ? "text-red-400" : "text-slate-500"}`}>{st.benched_for ? `${st.benched_for}s` : "—"}</td>
              <td className="py-2 pr-3 font-mono text-[11px] text-slate-500 max-w-[220px] truncate" title={st.last_error || ""}>{st.last_error || "—"}</td>
              <td className="py-2 text-right">
                {!active && s.enabled && r.status && r.status !== "stopped" && (
                  <Button data-testid={`force-switch-${i + 1}`} size="sm" variant="ghost" className="h-7 text-xs" onClick={() => act("switch", i + 1)}><Shuffle size={12} /> Switch</Button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
    <p className="text-[11px] text-slate-500 mt-2">7-day score = 100 × 0.85^failures (failures, limit refusals and blocklist refusals in the last 7 days). 85+ good · 60–84 fair · below 60 weak.</p>
    </>
  );
}

function Outputs({ c }) {
  const o = c.outputs || {};
  return (
    <div>
      <UrlRow label="HLS" url={o.hls} testid="output-hls" />
      <UrlRow label="MPEG-TS" url={o.ts} testid="output-ts" />
      <UrlRow label="Status" url={o.status} testid="output-status" />
      <div className="flex items-center gap-2 py-1.5 opacity-60">
        <span className="w-20 shrink-0 text-[10px] font-semibold uppercase tracking-wider text-slate-500">RTMP pull</span>
        <code className="flex-1 truncate text-xs text-slate-400">{o.rtmp_pull}</code>
        <Pill tone="amber">needs dedicated host</Pill>
      </div>
      <p className="text-[11px] text-slate-500 mt-2">Deny by default: only authorized servers granted this channel (and your dashboard session) can pull these URLs.</p>
      <div className="mt-4">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-2">RTMP push targets</div>
        {(c.push_targets || []).length === 0 && <p className="text-xs text-slate-500">None configured — add up to 3 in Edit.</p>}
        {(c.push_targets || []).map((t, i) => {
          const ps = c.push_status?.[i];
          const tone = !t.enabled ? "slate" : ps?.status === "connected" ? "green" : ps?.status === "failed" ? "red" : "amber";
          return (
            <div key={i} data-testid={`push-status-${i}`} className="flex items-center gap-2 py-1.5 border-b border-slate-800/60 last:border-0">
              <Pill tone={tone}>{t.enabled ? ps?.status || "starting" : "off"}</Pill>
              <code className="flex-1 truncate text-xs text-slate-300">{t.url}/<span className="text-slate-500">{t.key}</span></code>
              {ps?.last_error && <span className="text-[11px] font-mono text-slate-500 truncate max-w-[200px]" title={ps.last_error}>{ps.last_error}</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Timeline({ cid }) {
  const { data } = usePoll(`/admin/incidents?channel_id=${cid}&limit=60`, 4000);
  return (
    <div className="max-h-[360px] overflow-y-auto space-y-0" data-testid="channel-timeline">
      {(data || []).map((e) => (
        <div key={e.id} className="flex gap-3 py-1.5 border-b border-slate-800/50 text-xs">
          <span className="font-mono text-slate-500 w-[68px] shrink-0">{fmtTime(e.ts)}</span>
          <Pill tone={LEVEL_TONE[e.level]}>{e.kind}</Pill>
          <span className="text-slate-300">{e.message}</span>
        </div>
      ))}
      {data && data.length === 0 && <p className="text-xs text-slate-500">No events yet.</p>}
    </div>
  );
}

export default function ChannelDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { data: c, reload } = usePoll(`/admin/channels/${id}`, 2500);
  const [edit, setEdit] = useState(false);
  if (!c) return <div className="text-slate-500 font-mono text-sm">loading…</div>;
  const r = c.runtime || {};
  const running = r.status && r.status !== "stopped";

  const act = async (action, source) => {
    try { await api.post(`/admin/channels/${id}/action`, { action, source }); toast.success(`${action} sent`); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const del = async () => { await api.delete(`/admin/channels/${id}`); toast.success("Channel deleted"); nav("/channels"); };

  return (
    <div className="space-y-4">
      <button onClick={() => nav("/channels")} className="text-xs text-slate-400 hover:text-slate-200 flex items-center gap-1"><ArrowLeft size={13} /> Channels</button>
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold tracking-tight text-slate-100" data-testid="channel-title">{c.name}</h1>
            <HealthBadge health={r.health} status={r.status} testid="channel-detail-health" />
            {c.type === "news" && <Pill tone="blue">news</Pill>}
          </div>
          <div className="font-mono text-xs text-slate-500 mt-1">{c.slug} · audio {c.audio_policy} · failback {c.failback_window}s{r.switch_reason && <> · last switch: <span className="text-slate-300">{r.switch_reason}</span></>}</div>
        </div>
        <div className="flex gap-2">
          {running ? (
            <>
              <Button data-testid="channel-restart-btn" variant="outline" size="sm" className="border-slate-700" onClick={() => act("restart")}><RotateCcw size={13} /> Restart</Button>
              <Button data-testid="channel-stop-btn" variant="outline" size="sm" className="border-slate-700" onClick={() => act("stop")}><Square size={13} /> Stop</Button>
            </>
          ) : <Button data-testid="channel-start-btn" size="sm" className="bg-emerald-600 hover:bg-emerald-500" onClick={() => act("start")}><Play size={13} /> Start</Button>}
          <Button data-testid="channel-edit-btn" variant="outline" size="sm" className="border-slate-700" onClick={() => setEdit(true)}><Pencil size={13} /> Edit</Button>
          <AlertDialog>
            <AlertDialogTrigger asChild><Button data-testid="channel-delete-btn" variant="outline" size="sm" className="border-red-900 text-red-400"><Trash2 size={13} /></Button></AlertDialogTrigger>
            <AlertDialogContent className="bg-[#0F1722] border-slate-700">
              <AlertDialogHeader><AlertDialogTitle>Delete {c.name}?</AlertDialogTitle><AlertDialogDescription>Outputs stop immediately for every downstream server.</AlertDialogDescription></AlertDialogHeader>
              <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction data-testid="channel-delete-confirm" onClick={del} className="bg-red-600">Delete</AlertDialogAction></AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-2">
        <Stat label="Active source" value={r.active_source ? `#${r.active_source}` : "none"} testid="stat-active-source" tone={r.active_source === 1 ? "text-emerald-400" : r.active_source ? "text-amber-400" : "text-red-400"} />
        <Stat label="Media seq" value={r.media_sequence} testid="stat-media-seq" />
        <Stat label="Disc seq" value={r.disc_seq} testid="stat-disc-seq" />
        <Stat label="Segment age" value={r.segment_age != null ? `${r.segment_age}s` : null} testid="stat-seg-age" tone={r.segment_age > 12 ? "text-red-400" : undefined} />
        <Stat label="Input" value={r.upstream_kind} />
        <Stat label="Upstream bytes" value={fmtBytes(r.upstream_bytes)} />
        <Stat label="Hiccup retries" value={r.retries} />
        <Stat label="Audio" value={r.transcoding_audio ? "→ AAC" : "passthrough"} tone={r.audio_ok === false ? "text-amber-400" : undefined} />
      </div>

      <div className="grid xl:grid-cols-[1fr_1.4fr] gap-4">
        <Panel title="Live preview (output)">
          {running ? <HlsPlayer src={`${BACKEND}/api/live/${c.slug}/index.m3u8`} snapshot={`${BACKEND}/api/snapshot/${c.slug}.jpg`} /> : <div className="aspect-video grid place-items-center bg-black/50 rounded text-slate-500 text-sm">channel stopped</div>}
        </Panel>
        <Panel title="Source ladder" right={<span className="font-mono text-[11px] text-slate-500">uptime {fmtDur(r.uptime)}</span>}><Ladder c={c} act={act} /></Panel>
      </div>

      <div className="grid xl:grid-cols-3 gap-4">
        <Panel title="Outputs"><Outputs c={c} /></Panel>
        <Panel title="Playlist tail (index.m3u8)">
          <pre data-testid="playlist-tail" className="text-[11px] leading-relaxed text-slate-300 max-h-[360px] overflow-auto">{(c.playlist || "not running").split("\n").map((l, i) => (
            <div key={i} className={l.startsWith("#EXT-X-DISCONTINUITY") ? "text-amber-400" : l.startsWith("#") ? "text-slate-500" : "text-cyan-300"}>{l}</div>
          ))}</pre>
        </Panel>
        <Panel title="Event timeline"><Timeline cid={id} /></Panel>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="channel-cost">
        <Stat label="CPU (ffmpeg)" value={c.cost ? `${c.cost.cpu}%` : "—"} testid="cost-cpu" />
        <Stat label="Upstream in" value={c.cost ? fmtBps(c.cost.in_bps) : "—"} testid="cost-in" />
        <Stat label="Served out" value={c.cost ? fmtBps(c.cost.out_bps) : "—"} testid="cost-out" />
        <Stat label="Viewers (servers)" value={c.cost?.viewers ?? "—"} />
        <Stat label="RTMP pushes" value={c.cost?.pushes ?? "—"} />
      </div>
      <UsageCharts kind="channel" refId={id} testid="channel-usage" charts={[
        { title: "CPU %", series: [["cpu", "#06B6D4", "CPU"]], fmt: (v) => `${Number(v).toFixed(1)}%` },
        { title: "Bandwidth", series: [["in_bps", "#10B981", "in"], ["out_bps", "#F59E0B", "out"]], fmt: fmtBps },
      ]} />
      <ChannelForm open={edit} onOpenChange={setEdit} initial={c} onSaved={reload} />
    </div>
  );
}
